import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { HiX, HiArrowRight } from "react-icons/hi";
import { FilterBar, FilterField } from "../common/FilterControls";
import Add from "../Modal/notification/Add";

const NotificationTable = ({ columns, data, customers, onItemAdded, onSendNow = null }) => {
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState("Name");

  const allowedFilterColumns = [
    // { name: "Date", selector: "date" },
    { name: "Name", selector: "user_name" },
    { name: "User Type", selector: "role_name" },
    // { name: "Amount", selector: "amount" },
  ];

  // Function to truncate text and add ellipsis if it's too long
  const truncateText = (text, maxLength = 20) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // Modify columns to truncate text if needed
  const modifiedColumns = columns.map((column) => ({
    ...column,
    cell: (row, index) => {
      // For the # column, just return the index + 1
      if (column.name === "#") {
        return index + 1;
      }

      // FIX_RULES 10.3: a column's display format wins over its raw (sortable) selector value
      const cellValue = column.format
        ? column.format(row, index)
        : column.selector
        ? typeof column.selector === "function"
          ? column.selector(row, index) // Pass index to selector function
          : row[column.selector]
        : "";

      return (
        <div className="relative group">
          <span>{truncateText(cellValue?.toString())}</span>
          {cellValue?.toString().length > 20 && (
            <div className="absolute hidden group-hover:block z-10 bg-white p-2 border rounded shadow-lg max-w-xs break-words">
              {cellValue}
            </div>
          )}
        </div>
      );
    },
  }));

  const actionColumn = {
    id: "actions", name: "Actions",
    cell: (row) => {
      return (
        <div className="flex items-center gap-2">
          {/* P061: send a template that has not gone out yet at once (the page asks first, naming how many it reaches) */}
          {onSendNow && !["Sent", "Cancelled"].includes(row.status) && (
            <button
              type="button"
              onClick={() => onSendNow(row)}
              className="sgew-btn sgew-btn-sm sgew-btn-primary"
            >
              Send now
            </button>
          )}
          <button
            onClick={() => {
              setSelectedItemId(row.id);
              setOpenModal(true);
            }}
            className="flex items-center justify-center w-8 h-8 rounded-full text-[#0074ff] hover:bg-[#0074ff1a] transition duration-200"
          >
            <HiArrowRight className="w-5 h-5" />
          </button>
        </div>
      );
    },
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    right: true,
    ...(onSendNow ? { width: "150px" } : {}), // room for "Send now" beside the arrow
    style: {
      position: "sticky",
      right: "0px",
      zIndex: 1,
      backgroundColor: "inherit", // covers the cells that scroll under the pinned column (SGEW 17 Oct)
      boxShadow: "-10px 0 10px -10px rgba(0, 0, 0, 0.15)",
    },
  };

  // Now include actionColumn inside enhancedColumns
  const enhancedColumns = [...modifiedColumns, actionColumn];

  // Function to get column value by either string key or selector function
  const getColumnValue = (row, selector) => {
    if (typeof selector === "function") {
      return selector(row);
    }
    return row[selector] || "";
  };

  // Filter data by the selected column and filter text
  const filteredData = data.filter((item) => {
    const column = allowedFilterColumns.find(
      (col) => col.name === selectedColumn
    );

    if (!column) {
      return true; // No filter if no column is selected
    }

    const valueToFilter = getColumnValue(item, column.selector)
      .toString()
      .toLowerCase();
    return valueToFilter.includes(filterText.toLowerCase());
  });

  const handleClear = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText("");
    }
  };

  // Sub-header: pick the column, then type to filter it (SGEW 17 Oct: the shared filter-bar look - labelled, 38px high)
  const subHeaderComponent = (
    <FilterBar className="w-full py-2 text-left">
      <FilterField label="Search in" kind="select">
        <select
          className="sgew-control"
          value={selectedColumn}
          onChange={(e) => {
            setSelectedColumn(e.target.value);
            setFilterText(""); // Clear filter text when changing column
          }}
        >
          {allowedFilterColumns.map((col) => (
            <option key={col.selector} value={col.name}>
              {col.name}
            </option>
          ))}
        </select>
      </FilterField>
      <FilterField label="Search" kind="grow">
        <span className="relative block">
          <input
            className="sgew-control pr-8"
            type="text"
            placeholder={`Filter by ${selectedColumn}`}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {filterText && (
            <HiX
              className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              onClick={handleClear}
            />
          )}
        </span>
      </FilterField>
    </FilterBar>
  );

  return (
    <div className="font-poppins">
      <div className="overflow-hidden  !bg-white rounded-[0.42vw]">
        <DataTable
          columns={enhancedColumns}
          data={filteredData}
          pagination
          subHeader
          subHeaderComponent={subHeaderComponent}
          paginationResetDefaultPage={resetPaginationToggle}
          persistTableHead
          customStyles={{
            rows: {
              style: {
                minHeight: "60px",
                fontSize: "clamp(12px,0.93vw, 1rem)",
              },
            },
            headCells: {
              style: {
                fontWeight: 600,
                textAlign: "center",
                whiteSpace: "normal",
                fontSize: "clamp(14px, 1.1vw, 1.1rem)",
                color: "#10131e",
              },
            },
          }}
        />
      </div>
      {openModal && (
        <Add
          open={openModal}
          handleClose={() => setOpenModal(false)}
          selectedItemId={selectedItemId}
          onItemAdded={onItemAdded}
        />
      )}
    </div>
  );
};

export default NotificationTable;
